import { isWeb } from "@helpers/platform";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { Box, DialogWrapper, Loader } from "@memorang/ui";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { Dialog as PaperDialog, Text } from "react-native-paper";
import {
	type getProethicaModuleListProps,
	getProethicaOrderedEntries,
} from "../helpers/proethica-module-list";
import { useProethicaModuleList } from "../hooks/useProethicaModuleList";
import { ProethicaModuleListItem } from "./ProethicaModuleListItem";
import { PurchaseDialog } from "./PurchaseDialog";

type ProethicaModule = ReturnType<typeof getProethicaModuleListProps>;

type Props = {
	visible: boolean;
	handleClose: () => void;
};

export const ProethicaQuickStudyDialog = ({ visible, handleClose }: Props) => {
	const { t } = useTranslation();
	const router = useRouter();
	const { data: mixedItemDist, isLoading } = useMixedItemDist();
	const {
		getModulesGroupedByStatus,
		startModule,
		startingModuleId,
		resolveModuleItems,
	} = useProethicaModuleList();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const items = getProethicaOrderedEntries(mixedItemDist?.exams);

	const sections = useMemo(
		() => getModulesGroupedByStatus(resolveModuleItems(items), t),
		[items, t, getModulesGroupedByStatus, resolveModuleItems],
	);

	const handleModulePress = (module: ProethicaModule) => {
		if (module.hasAccess === false) {
			if (isWeb) {
				setShowPurchaseDialog(true);
				return;
			}
			handleClose();
			router.push({
				pathname: "/purchase",
				params: { productType: "STUDY_PACK" },
			});
			return;
		}

		if (module.disabled) {
			return;
		}

		handleClose();
		// startModule mirrors the main list: COMPLETE opens the session report
		// (a completed pretest with no resolvable session id shows an error
		// instead of restarting), ACTIVE modules resume in place, NOT_STARTED
		// creates a fresh session.
		startModule(module.id);
	};

	return (
		<>
			<DialogWrapper
				visible={visible}
				handleClose={handleClose}
				width={520}
				showCloseButton
			>
				<PaperDialog.Title>{t("dashboard.quickStudy.title")}</PaperDialog.Title>
				<PaperDialog.ScrollArea style={{ paddingHorizontal: 0 }}>
					<ScrollView contentContainerStyle={{ paddingBottom: 16 }}>
						{isLoading || !mixedItemDist ? (
							<Loader />
						) : (
							<Box style={{ gap: 8 }}>
								<Text
									variant="bodyMedium"
									style={{ paddingHorizontal: 24, paddingBottom: 8 }}
								>
									{t("dashboard.quickStudy.subtitle")}
								</Text>
								{sections.map((section) => (
									<Box key={section.status}>
										<Text
											variant="titleSmall"
											style={{
												paddingHorizontal: 24,
												paddingVertical: 8,
												opacity: 0.8,
											}}
										>
											{section.title}
										</Text>
										{section.modules.map((module) => (
											<ProethicaModuleListItem
												key={module.id}
												title={module.title}
												subtitle={module.subtitle}
												statusLabel={module.statusLabel}
												status={module.status}
												cta={module.cta}
												thumbnailUrl={module.thumbnailUrl}
												hasAccess={module.hasAccess}
												disabled={module.disabled}
												loading={startingModuleId === module.id}
												onPress={() => handleModulePress(module)}
											/>
										))}
									</Box>
								))}
							</Box>
						)}
					</ScrollView>
				</PaperDialog.ScrollArea>
			</DialogWrapper>
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setShowPurchaseDialog(false)}
					productType="STUDY_PACK"
				/>
			)}
		</>
	);
};
